import styled, { createGlobalStyle, css } from 'styled-components';

import colors from 'app/styles/theme/colors';
import {
  radii,
  spacing,
  fontSizes,
  fontFamily,
} from 'app/components/customers/styles/scales';
import { rgba } from 'app/components/customers/styles/rgba';

const { gray, green, red } = colors.designSystem;

const focusRing = `0 0 0 2px ${rgba(green[500], 0.4)}`;
const focusRingInvalid = `0 0 0 2px ${rgba(red[500], 0.3)}`;

export const GlobalStyles = createGlobalStyle`
  html,
  body,
  #root {
    background: ${gray[0]} !important;
    margin: 0;
    padding: 0;
    width: 100%;
    min-height: 100%;
  }

  * {
    box-sizing: border-box;
  }
`;

export const PaymentPage = styled.div`
  min-height: 100vh;
  background: ${gray[0]};
  font-family: ${fontFamily.body};
  color: ${gray[950]};
  width: 100%;
  position: relative;
`;

export const PaymentHeader = styled.header`
  background: ${gray[0]};
  border-bottom: 1px solid ${gray[250]};
  padding: ${spacing[16]} 0;
  width: 100vw;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
`;

export const HeaderContent = styled.div`
  max-width: 1360px;
  margin: 0 auto;
  padding: 0 ${spacing[32]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[16]};
  flex-wrap: wrap;

  @media (max-width: 480px) {
    padding: 0 ${spacing[16]};
  }
`;

export const Logo = styled.img`
  height: 32px;
  width: auto;

  @media (max-width: 480px) {
    height: 28px;
  }
`;

export const SecurityNotice = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[8]};
  color: ${gray[600]};
  font-size: ${fontSizes[13]};
  font-family: ${fontFamily.body};

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const PaymentContainer = styled.div`
  max-width: 1360px;
  margin: ${spacing[32]} auto;
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(420px, 1fr);
  gap: ${spacing[48]};
  padding: 0 ${spacing[32]};
  align-items: stretch;

  @media (max-width: 991px) {
    grid-template-columns: 1fr;
    margin: ${spacing[32]} auto;
    gap: ${spacing[24]};
  }

  @media (max-width: 480px) {
    padding: 0 ${spacing[16]};
    margin: ${spacing[24]} auto;
  }
`;

export const SectionHeader = styled.div`
  grid-column: 1 / -1;
  margin-bottom: ${spacing[8]};

  h1 {
    font-family: ${fontFamily.title};
    font-size: ${fontSizes[32]};
    font-weight: 700;
    line-height: 1.15;
    margin: 0 0 ${spacing[8]} 0;
    color: ${gray[950]};

    @media (max-width: 480px) {
      font-size: ${fontSizes.xl2};
    }
  }

  p {
    font-size: ${fontSizes[14]};
    color: ${gray[700]};
    margin: 0;
  }
`;

export const PaymentColumn = styled.div`
  grid-column: 1;
  display: flex;
  flex-direction: column;
  gap: ${spacing[24]};
  min-width: 0;

  @media (max-width: 991px) {
    order: 1;
  }
`;

export const Card = styled.section`
  background: ${gray[0]};
  border: 1px solid ${gray[250]};
  border-radius: ${radii.medium};
  padding: ${spacing[24]};

  @media (max-width: 480px) {
    padding: ${spacing[16]};
  }
`;

export const SectionTitle = styled.h3`
  font-family: ${fontFamily.title};
  font-size: ${fontSizes[16]};
  font-weight: 600;
  color: ${gray[950]};
  margin: 0 0 ${spacing[16]} 0;
`;

export const SectionHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[8]};
`;

export const SectionNumber = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: ${radii.full};
  background: ${gray[200]};
  color: ${gray[700]};
  font-family: ${fontFamily.body};
  font-size: ${fontSizes[13]};
  font-weight: 600;
`;

export const SectionHeading = styled(SectionTitle)`
  margin: 0;
`;

export const SectionBlock = styled.div`
  margin-top: ${spacing[24]};
`;

export const FormRow = styled.div`
  display: grid;
  gap: ${spacing[16]};
  margin-bottom: ${spacing[16]};

  &.full-width {
    grid-template-columns: 1fr;
  }

  &.two-columns {
    grid-template-columns: 1fr 1fr;
  }

  &.three-columns {
    grid-template-columns: 1fr 1fr 1fr;
  }

  &:last-child {
    margin-bottom: 0;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: ${spacing[8]};
    min-width: 0;

    label {
      font-size: ${fontSizes[13]};
      font-weight: 500;
      color: ${gray[950]};

      .text-danger {
        color: ${red[500]};
        margin-left: 2px;
      }
    }

    input,
    select {
      width: 100%;
      padding: ${spacing[8]} ${spacing[12]};
      border: 1px solid ${gray[400]};
      border-radius: ${radii.small};
      font-size: ${fontSizes[14]};
      font-family: ${fontFamily.body};
      color: ${gray[950]};
      background: ${gray[0]};
      min-width: 0;
      transition: border-color 0.15s, box-shadow 0.15s;

      &::placeholder {
        color: ${gray[500]};
      }

      &:focus-visible {
        outline: none;
        border-color: transparent;
        box-shadow: 0 0 0 2px ${green[500]};
      }

      &.is-invalid {
        border-color: ${red[500]};

        &:focus-visible {
          box-shadow: ${focusRingInvalid};
        }
      }
    }
    .invalid-feedback {
      font-size: ${fontSizes[13]};
      font-weight: 500;
      color: ${red[500]};
    }
  }

  @media (max-width: 767px) {
    &.three-columns {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 480px) {
    &.two-columns {
      grid-template-columns: 1fr;
    }
  }
`;

export const CardNumberFieldWrapper = styled.div`
  position: relative;

  input {
    padding-right: 56px;
  }
`;

export const CardBrandIcon = styled.img`
  position: absolute;
  right: ${spacing[12]};
  top: 50%;
  transform: translateY(-50%);
  height: 20px;
  width: auto;
  pointer-events: none;
`;

export const PaymentMethodOptions = styled.div`
  display: flex;
  gap: ${spacing[12]};
  flex-wrap: wrap;
`;

export const PaymentMethodOption = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing[8]};
  flex: 1 1 0;
  min-width: 160px;
  padding: ${spacing[12]} ${spacing[16]};
  border: 1px solid ${(props) => (props.$active ? green[500] : gray[250])};
  background: ${(props) => (props.$active ? green[50] : gray[0])};
  border-radius: ${radii.small};
  cursor: pointer;
  font-family: ${fontFamily.body};
  font-size: ${fontSizes[14]};
  font-weight: ${(props) => (props.$active ? 600 : 500)};
  color: ${gray[950]};
  transition: border-color 0.15s, background-color 0.15s;

  &:hover {
    border-color: ${green[500]};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${focusRing};
  }

  @media (max-width: 480px) {
    flex: 1 1 100%;
  }
`;

export const PaymentMethodIcon = styled.span<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: ${radii.small};
  background: ${(props) => (props.$active ? green[100] : gray[100])};
  color: ${(props) => (props.$active ? green[600] : gray[700])};

  svg {
    width: 16px;
    height: 16px;
    fill: currentColor;
  }
`;

const spaceBetweenRow = css`
  display: flex;
  justify-content: space-between;
  gap: ${spacing[16]};
`;

export const PurchaseDetails = styled.div`
  grid-column: 2;
  min-width: 0;
  height: 100%;
  background: ${gray[100]};
  border-radius: ${radii.medium};
  padding: ${spacing[32]};
  display: flex;
  flex-direction: column;

  .purchase-details-container {
    width: 100%;
    background: ${gray[0]};
    border: 1px solid ${gray[250]};
    border-radius: ${radii.medium};
    padding: ${spacing[24]};
  }

  .purchase-details-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${spacing[16]};
    margin-bottom: ${spacing[24]};

    h3 {
      margin: 0;
      font-size: 18px;
    }

    .info-icon {
      width: 18px;
      height: 18px;
      border: 1px solid ${gray[600]};
      border-radius: 50%;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      color: ${gray[700]};
      font-size: 12px;
      font-weight: 600;
      line-height: 1;
    }
  }

  /*
   * Timeline verde do detalhamento do pedido.
   * Cada .purchase-section desenha uma bolinha.
   * A primeira section também desenha a linha até a segunda bolinha.
   */
  .purchase-timeline {
    display: flex;
    flex-direction: column;
  }

  .purchase-section {
    position: relative;
    padding-left: ${spacing[32]};

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 4px;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background: ${green[500]};
      z-index: 2;
    }

    &:first-child::after {
      content: '';
      position: absolute;
      left: 5.5px;
      top: 10px;
      bottom: -30px;
      width: 1px;
      background: ${green[500]};
      z-index: 1;
    }

    & + .purchase-section {
      margin-top: ${spacing[24]};
    }
  }

  .price-row-label {
    margin: 0 0 ${spacing[8]} 0;
    font-size: ${fontSizes[14]};
    font-weight: 600;
    color: ${gray[950]};
  }

  .price-row {
    ${spaceBetweenRow}
    align-items: baseline;
    font-size: ${fontSizes[14]};
    color: ${gray[950]};

    & + .price-row {
      margin-top: ${spacing[8]};
    }

    .item-description {
      min-width: 0;
    }

    .item-quantity {
      color: ${gray[700]};
      text-decoration: underline;
      text-underline-offset: 2px;
    }

    .price-value {
      color: ${gray[700]};
      white-space: nowrap;
    }
  }

  .coupon-row,
  .summary-total-row {
    ${spaceBetweenRow}
    align-items: baseline;
    font-size: ${fontSizes[14]};
    color: ${gray[950]};
  }

  .coupon-row {
    margin-bottom: ${spacing[12]};
  }

  .summary-total-row {
    font-weight: 500;
  }

  .purchase-details-footer {
    width: 100%;
    margin-top: auto;
    padding-top: ${spacing[32]};
  }

  .payment-today-row {
    ${spaceBetweenRow}
    align-items: center;
    margin-bottom: ${spacing[16]};
    color: ${gray[950]};
    font-size: ${fontSizes[14]};
    font-weight: 600;

    strong {
      flex-shrink: 0;
      font-size: 18px;
      font-weight: 700;
    }
  }

  @media (max-width: 991px) {
    grid-column: 1;
    order: 2;
    height: auto;
  }

  @media (max-width: 480px) {
    padding: ${spacing[16]};

    .purchase-details-container {
      padding: ${spacing[16]};
    }

    .purchase-section {
      padding-left: ${spacing[24]};
    }

    .price-row,
    .coupon-row,
    .summary-total-row,
    .payment-today-row {
      gap: ${spacing[8]};
    }
  }
`;

export const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${gray[250]};
  margin: ${spacing[16]} 0;
`;

export const SubmitSection = styled.div`
  margin: 0;
  padding: 0;
  border: none;
  display: flex;
  flex-direction: column;
  gap: ${spacing[16]};

  /*
   * Mantém o botão acima dos termos mesmo se o JSX antigo ainda estiver
   * com <TermsSection /> antes de <ConfirmPaymentBtn />.
   */
  > button {
    order: 1;
  }

  > div {
    order: 2;
  }
`;

export const TermsSection = styled.div`
  margin: 0;

  .checkbox-container {
    display: flex;
    align-items: flex-start;
    gap: ${spacing[12]};
    cursor: pointer;

    input[type='checkbox'] {
      appearance: none;
      -webkit-appearance: none;
      margin: 2px 0 0 0;
      width: 18px;
      height: 18px;
      flex-shrink: 0;
      border: 1px solid ${gray[400]};
      border-radius: ${radii.extraSmall};
      background: ${gray[0]};
      cursor: pointer;
      position: relative;
      transition: border-color 0.15s, background-color 0.15s;

      &:checked {
        background: ${green[500]};
        border-color: ${green[500]};
      }

      &:checked::after {
        content: '\2713';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        color: ${gray[0]};
        font-size: 12px;
        line-height: 1;
      }

      &:focus-visible {
        box-shadow: ${focusRing};
      }
    }

    .terms-text {
      font-size: ${fontSizes[13]};
      color: ${gray[700]};
      line-height: 1.5;

      a {
        color: ${green[500]};
        text-decoration: none;

        &:hover {
          text-decoration: underline;
        }
      }
    }
  }
`;

export const ModalOverlay = styled.div<{ $open: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  display: ${(props) => (props.$open ? 'flex' : 'none')};
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  z-index: 99;
`;

export const ModalDialog = styled.div`
  position: relative;
  width: 100%;
  max-width: 460px;
  background: ${gray[0]};
  border-radius: ${radii.medium};
  padding: ${spacing[32]};
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  font-family: ${fontFamily.body};
  animation: modalAppear 0.3s ease-out;

  @keyframes modalAppear {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(-12px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }

  @media (max-width: 480px) {
    max-width: 90%;
    padding: ${spacing[24]};
  }
`;

export const ConfirmPaymentBtn = styled.button`
  width: 100%;
  background: ${green[500]};
  color: ${gray[0]};
  border: none;
  padding: ${spacing[12]} ${spacing[24]};
  font-size: ${fontSizes[16]};
  font-weight: 600;
  font-family: ${fontFamily.body};
  border-radius: ${radii.small};
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover:not(:disabled) {
    background: ${green[600]};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${focusRing};
  }

  &:disabled {
    background: ${gray[200]};
    color: ${gray[600]};
    cursor: not-allowed;
  }
`;
